import {
    Box,
    Button,
    Card,
    CardBody,
    CardHeader,
    Flex,
    FormControl,
    FormLabel,
    Heading,
    Image,
    Input,

    Select,
    useToast,
    VStack,
} from "@chakra-ui/react";
import { Form, Formik } from "formik";
import * as Yup from "yup";

import { useEffect, useState } from "react";

import { generatePublicUrl, uploadFileToAzure } from "@/services/azureStorage.service";
import { createNewsletter, getNewsletterById, NewsletterCreate, NewsLetterResponse, NewsletterType, updateNewsletter } from "@/services/newsletter";
import { AttachmentIcon } from "@chakra-ui/icons";
import { useParams } from "react-router-dom";
import InputField from "../TextInputField";


// Validation schema específico da Newsletter
const newsletterValidationSchema = Yup.object().shape({
    title: Yup.string()
        .nullable(),
    content: Yup.string().nullable(),
    type: Yup.mixed<NewsletterType>()
        .oneOf(Object.values(NewsletterType) as NewsletterType[], "Tipo inválido")
        .required("Tipo de conteúdo é obrigatório"),
    imageUrl: Yup.mixed()
        .nullable()
        .when("type", {
            is: (val: any) => val === NewsletterType.CAMPANHA,
            then: (schema) => schema.required("Imagem é obrigatória para campanha"),
        }),
    thumbnail: Yup.mixed()
        .nullable()
        .when("type", {
            is: (val: any) => val === NewsletterType.CAMPANHA,
            then: (schema) => schema.required("Thumbnail é obrigatório para campanha"),
        }),
    labelLink: Yup.string().nullable(),
    link: Yup.string()
        .nullable()
        .url("Link deve ser uma URL válida"),
});

export default function NewsletterEditor() {
    const [actualNewsletter, setActualNewsletter] = useState<
        NewsLetterResponse | undefined
    >(undefined);

    const toast = useToast();

    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
    const { newsletterId } = useParams();
    const isNew = newsletterId === "new" || newsletterId === "novo";

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (!isNew) {
                    const newsletter = await getNewsletterById(newsletterId!);

                    if (newsletter) {
                        setActualNewsletter(newsletter);
                    }
                }
            } catch (err: any) {
                toast({
                    title: "Erro ao carregar dados",
                    description: err.message,
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        };

        fetchData();
    }, [newsletterId]);

    return (
        <Flex direction="column" width="100%">
            {/* <BackButton routeTo="/consignado/produtos" /> */}

            <Card width="100%" display="flex" flexDir="column">
                <CardHeader width="100%">
                    <Heading fontFamily="Lexend Deca" fontSize="3xl" as="b">
                        {!isNew ? "Editar Newsletter" : "Adicionar Newsletter"}
                    </Heading>
                </CardHeader>
                <CardBody height="100%">
                    <Formik
                        enableReinitialize={true}
                        // 1. Novos initialValues baseados nos campos fornecidos
                        initialValues={{
                            id: actualNewsletter?.id ?? undefined,
                            title: actualNewsletter?.title ?? "",
                            content: actualNewsletter?.content ?? "",
                            imageUrl: actualNewsletter?.imageUrl ?? "",
                            thumbnail: actualNewsletter?.thumbnail ?? "",
                            type: actualNewsletter?.type ?? NewsletterType.NOTICIA,
                            labelLink: actualNewsletter?.labelLink ?? "",
                            link: actualNewsletter?.link ?? "",
                        }}
                        validationSchema={newsletterValidationSchema}
                        onSubmit={async (values, { setSubmitting }) => {
                            setSubmitting(true);

                            // 2. Criação do novo Newsletter com os campos corretos
                            const newsletterData: Partial<NewsletterCreate> = {
                                id: values.id,
                                title: values.title,
                                content: values.content,
                                imageUrl:
                                    typeof values.imageUrl === "string"
                                        ? values.imageUrl
                                        : undefined,
                                type: values.type,
                                labelLink: values.labelLink,
                                link: values.link,
                                thumbnail:
                                    typeof values.thumbnail === "string"
                                        ? values.thumbnail
                                        : undefined,
                            };

                            try {
                                // Upload de imagem somente quando for um arquivo novo (File)
                                if (values.imageUrl && typeof values.imageUrl !== "string") {
                                    const file = values.imageUrl as File;
                                    const { uploadUrl: imageUploadUrl, publicUrl: imagePublicUrl } = await generatePublicUrl(file.name, "documents");
                                    await uploadFileToAzure(imageUploadUrl, file);

                                    newsletterData.imageUrl = imagePublicUrl;
                                    values.imageUrl = imagePublicUrl as any;
                                }

                                // Upload de thumbnail somente quando for um arquivo novo (File)
                                if (values.thumbnail && typeof values.thumbnail !== "string") {
                                    const file = values.thumbnail as File;

                                  
                                    const { uploadUrl: thumbUploadUrl, publicUrl: thumbPublicUrl } =
                                        await generatePublicUrl(file.name, "documents");

                                   
                                    await uploadFileToAzure(thumbUploadUrl, file);

                                    newsletterData.thumbnail = thumbPublicUrl;
                                    values.thumbnail = thumbPublicUrl as any;
                                }

                                !isNew
                                    ? await updateNewsletter(values.id!, newsletterData as NewsletterCreate)
                                    : await createNewsletter(newsletterData as NewsletterCreate);
                                toast({
                                    title: `Newsletter ${!isNew ? "editado" : "cadastrado"} com sucesso`,
                                    description: `Newsletter ${newsletterData.title} foi ${!isNew ? "editado" : "cadastrado"}!`,
                                    status: "success",
                                    duration: 3000,
                                    isClosable: true,
                                });

                                // navigate("/consignado/produtos");
                            } catch (error: any) {
                                toast({
                                    title: "Erro ao salvar produto",
                                    description: error.message,
                                    status: "error",
                                    duration: 3000,
                                    isClosable: true,
                                });
                            }
                        }}
                    >
                        {({ errors, touched, setFieldValue, values, isSubmitting }) => (
                            <Form>
                                <VStack spacing={8} minW="100%">
                                    {/* 3. Ajuste do Layout e dos Campos */}

                                    {/* PRIMEIRA LINHA */}
                                    <Flex gap={2} width="100%" >
                                        <InputField
                                            name="title"
                                            label="Titulo"
                                            placeholder="Titulo"
                                            value={values.title}
                                            error={errors.title}
                                            touched={touched.title}
                                            setFieldValue={setFieldValue}
                                            isUpperCase
                                        />
                                        <FormControl id="type" isRequired isInvalid={!!errors.type && !!touched.type}>
                                            <FormLabel>Tipo de Conteúdo</FormLabel>
                                            <Select
                                                placeholder="Selecione o tipo"
                                                value={values.type || ""}
                                                onChange={(e) => setFieldValue("type", e.target.value)}
                                            >
                                                <option value={NewsletterType.DESTAQUE}>DESTAQUE</option>
                                                <option value={NewsletterType.CAMPANHA}>CAMPANHA</option>
                                                {/* <option value={NewsletterType.NOTICIA}>NOTÍCIA</option> */}

                                            </Select>
                                            {errors.type && touched.type && (
                                                <Box mt={1} color="red.500" fontSize="sm">
                                                    {errors.type}
                                                </Box>
                                            )}
                                        </FormControl>
                                    </Flex>

                                    {/* SEGUNDA LINHA - Conteúdo */}
                                    <Flex gap="2rem" width="100%" zIndex={9999}>
                                        <FormControl isInvalid={!!errors.content && !!touched.content}>
                                            <FormLabel>Conteúdo</FormLabel>
                                            <textarea
                                                cols={80}
                                                rows={6}
                                                value={values.content}
                                                onChange={(e) => setFieldValue("content", e.target.value)}
                                                style={{
                                                    border: "1px solid #CBD5E0",
                                                    borderRadius: "4px",
                                                    padding: "8px",
                                                    width: "100%",
                                                    fontFamily: "inherit",
                                                }}
                                            />
                                            {errors.content && touched.content && (
                                                <Box mt={1} color="red.500" fontSize="sm">
                                                    {errors.content}
                                                </Box>
                                            )}
                                        </FormControl>
                                        <FormControl isRequired={values.type === NewsletterType.CAMPANHA} isInvalid={!!errors.imageUrl && !!touched.imageUrl}>
                                            <FormLabel fontFamily="Lexend Deca">Imagem</FormLabel>
                                            <Flex direction="column" align="flex-start">
                                                <Button
                                                    leftIcon={<AttachmentIcon />}
                                                    variant="outline"
                                                    onClick={() => {
                                                        document.getElementById("imageUpload")?.click();
                                                    }}
                                                >
                                                    {values.imageUrl
                                                        ? "Alterar Imagem"
                                                        : "Selecionar Imagem"}
                                                </Button>
                                                <Input
                                                    id="imageUpload"
                                                    type="file"
                                                    accept="image/*"
                                                    display="none"
                                                    onChange={(event) => {
                                                        const file = event.currentTarget.files?.[0] || null;
                                                        setFieldValue("imageUrl", file);
                                                        setImagePreview(
                                                            file ? URL.createObjectURL(file) : null
                                                        );
                                                    }}
                                                />
                                                {/* {values.imageUrl && (
                                                    <Text mt="0.5rem">{values.imageUrl.name}</Text>
                                                )} */}
                                                {values.imageUrl && (
                                                    <Box
                                                        mt="1rem"
                                                        maxW={{ base: "6.25rem", md: "9.375rem" }}
                                                        borderRadius="md"
                                                        borderWidth="0.0625rem"
                                                        overflow="hidden"
                                                    >
                                                        <Image
                                                            src={
                                                                imagePreview ||
                                                                (typeof values.imageUrl === "string"
                                                                    ? values.imageUrl
                                                                    : "")
                                                            }
                                                            alt="Logo Preview"
                                                            width="100%"
                                                            height="auto"
                                                            display="block"
                                                        />
                                                    </Box>
                                                )}
                                                {errors.imageUrl && touched.imageUrl && (
                                                    <Box mt={1} color="red.500" fontSize="sm">
                                                        {errors.imageUrl as string}
                                                    </Box>
                                                )}
                                            </Flex>
                                        </FormControl>
                                    </Flex>

                                    {/* TERCEIRA LINHA - Link */}
                                    <Flex gap="2rem" width="100%" justifyContent="flex-start">
                                        <Flex width='100%' flexDirection='column'>
                                            <InputField
                                                name="labelLink"
                                                label="Nome do Link"
                                                placeholder="Nome do link"
                                                value={values.labelLink}
                                                error={errors.labelLink}
                                                touched={touched.labelLink}
                                                setFieldValue={setFieldValue}
                                                isUpperCase
                                                width="100%"
                                            />
                                            <InputField
                                                name="link"
                                                label="Link"
                                                placeholder="Link"
                                                value={values.link}
                                                error={errors.link}
                                                touched={touched.link}
                                                setFieldValue={setFieldValue}
                                                isUpperCase
                                                width="100%"
                                            />
                                        </Flex>
                                        <FormControl isRequired={values.type === NewsletterType.CAMPANHA} isInvalid={!!errors.thumbnail && !!touched.thumbnail}>
                                            <FormLabel fontFamily="Lexend Deca">Thumbnail</FormLabel>
                                            <Flex direction="column" align="flex-start">
                                                <Button
                                                    leftIcon={<AttachmentIcon />}
                                                    variant="outline"
                                                    onClick={() => {
                                                        document.getElementById("thumbnailUpload")?.click();
                                                    }}
                                                >
                                                    {values.thumbnail
                                                        ? "Alterar Imagem"
                                                        : "Selecionar Imagem"}
                                                </Button>
                                                <Input
                                                    id="thumbnailUpload"
                                                    type="file"
                                                    accept="image/*"
                                                    display="none"
                                                    onChange={(event) => {
                                                        const file = event.currentTarget.files?.[0] || null;

                                                        if (!file) {
                                                            setFieldValue("thumbnail", null);
                                                            setThumbnailPreview(null);
                                                            return;
                                                        }

                                                        const imageUrl = URL.createObjectURL(file);
                                                        const img = document.createElement("img");

                                                        img.onload = () => {
                                                            const { width, height } = img;

                                                            if (width > 350 || height > 350) {
                                                                toast({
                                                                    title: "Thumbnail muito grande",
                                                                    description:
                                                                        "A imagem do thumbnail deve ter no máximo 350x350 pixels.",
                                                                    status: "error",
                                                                    duration: 4000,
                                                                    isClosable: true,
                                                                });

                                                                setFieldValue("thumbnail", null);
                                                                setThumbnailPreview(null);
                                                                return;
                                                            }

                                                            setFieldValue("thumbnail", file);
                                                            setThumbnailPreview(imageUrl);
                                                        };

                                                        img.onerror = () => {
                                                            toast({
                                                                title: "Erro ao carregar imagem",
                                                                description:
                                                                    "Não foi possível validar o tamanho da imagem do thumbnail.",
                                                                status: "error",
                                                                duration: 4000,
                                                                isClosable: true,
                                                            });

                                                            setFieldValue("thumbnail", null);
                                                            setThumbnailPreview(null);
                                                        };

                                                        img.src = imageUrl;
                                                    }}
                                                />
                                                {/* {values.thumbnail && (
                                                    <Text mt="0.5rem">{values.thumbnail.name}</Text>
                                                )} */}
                                                {values.thumbnail && (
                                                    <Box
                                                        mt="1rem"
                                                        maxW={{ base: "6.25rem", md: "9.375rem" }}
                                                        borderRadius="md"
                                                        borderWidth="0.0625rem"
                                                        overflow="hidden"
                                                    >
                                                        <Image
                                                            src={
                                                                thumbnailPreview ||
                                                                (typeof values.thumbnail === "string"
                                                                    ? values.thumbnail
                                                                    : "")
                                                            }
                                                            alt="Logo Preview"
                                                            width="100%"
                                                            height="auto"
                                                            display="block"
                                                        />
                                                    </Box>
                                                )}
                                                {errors.thumbnail && touched.thumbnail && (
                                                    <Box mt={1} color="red.500" fontSize="sm">
                                                        {errors.thumbnail as string}
                                                    </Box>
                                                )}
                                            </Flex>
                                        </FormControl>
                                    </Flex>


                                    {/* SEXTA LINHA - Rebate/Taxa Mensal/Switch */}
                                    <Flex gap="2rem" width="100%" alignItems="center">

                                    </Flex>



                                    {/* Botões de Ação */}
                                    <Flex justify="center" gap="2rem">


                                        <Button
                                            color={"white"}
                                            type="submit"
                                            bg={"secondary"}
                                            _hover={{ bg: "#1e3f5b" }}
                                            width="16rem"
                                            isLoading={isSubmitting}
                                            loadingText="Salvando"
                                        >
                                            {!isNew ? "Salvar Alterações" : "Cadastrar Notícia"}
                                        </Button>
                                    </Flex>
                                </VStack>
                            </Form>
                        )}
                    </Formik>
                </CardBody>
            </Card>
        </Flex>
    );
}
